import { locale } from '../lib/locale';

/**
 * Testfamilien (1.17): the invitation hint for families and Mike's /admin page. German is the
 * master; English (1.19) has the same keys – /admin stays German (only Mike).
 */
export const TESTFAMILY_TEXT = {
  de: {
    invited: 'Ihr seid als Testfamilie eingeladen – legt jetzt euer Konto an.',
    invitedSetup: 'Ihr seid als Testfamilie eingeladen – legt jetzt eure Familie an.',
    member: 'Ihr seid Testfamilie – während des Tests ist alles kostenlos.',
  },
  en: {
    invited: 'You’re invited as a pilot family – create your account now.',
    invitedSetup: 'You’re invited as a pilot family – set up your family now.',
    member: 'You’re a pilot family – everything is free during the pilot.',
  },
} as const;
export const testFamilyText = TESTFAMILY_TEXT[locale];

export const adminText = {
  title: 'Admin',
  waitlistTitle: 'Warteliste und Testfamilien',
  // tabs (Mike, 26.10.: "sinnvoll strukturieren, ggf. in Tabs")
  tabsLabel: 'Bereich',
  tabs: {
    uebersicht: 'Übersicht',
    warteliste: 'Warteliste',
    familien: 'Familien',
    konten: 'Konten',
    ki: 'KI',
  },
  ai: {
    // the cost cap (1.28.1, Mike 27.10.: "tracken können")
    budget: {
      title: 'Kostengrenze',
      hint: 'Grenze pro Tag = Grundbetrag + Familien mit KI × Budget je Familie. Jede Familie hat dasselbe Budget auch als eigene Grenze. Darüber macht die KI bis Mitternacht Pause; bei 80 % und 100 % kommt eine Mail.',
      base: 'Grundbetrag pro Tag (€)',
      family: 'Budget je Familie und Tag (€)',
      save: 'Speichern',
      saved: 'Gespeichert – gilt ab dem nächsten Aufruf.',
      today: (spent: string, limit: string, families: string) =>
        `Heute: ${spent} von ${limit} · ${families} Familien mit KI`,
      history: 'Verlauf (30 Tage)',
      day: 'Tag',
      cost: 'Kosten',
      limit: 'Grenze',
      calls: 'Aufrufe',
      atLimit: 'Familien an Grenze',
      blocked: 'Abgewiesen',
      none: 'Noch keine Aufrufe gebucht.',
      tight: 'Familien kamen an ihre Grenze – Budget je Familie prüfen.',
      notLive:
        'Die Kostengrenze ist auf dem Server noch nicht live – sie erscheint hier, sobald der Server-Teil von 1.28.1 läuft.',
    },
    measuringTitle: 'Messung',
    measuringHint:
      'Jeder KI-Aufruf der Familien: Aufgabe, Modell, Versuche, Dauer, Tokens – keine Inhalte, keine Familie. Kosten geschätzt nach Listenpreis.',
    loading: 'Wird geladen …',
    task: 'Aufgabe',
    models: 'Modelle',
    calls7: 'Aufrufe 7 Tage',
    firstTry: 'im ersten Anlauf',
    failed: 'gescheitert',
    duration: 'Dauer Ø',
    cost30: 'Kosten 30 Tage',
    tasks: {
      sentence: 'Satz / Sprache',
      photo: 'Foto (Kalender, Stundenplan, Brief)',
      voucher: 'Gutschein-Foto',
      recipePhoto: 'Rezept-Foto',
      recipeText: 'Rezept-Text',
      ideas: 'Essensideen',
    },
    sentencesTitle: 'Testsatz: Sätze',
    sentencesHint: (n: number) =>
      `${n} Sätze, wie Familien sie schreiben und sprechen (mit Hörfehlern), je einmal an Flash-Lite und Flash. Geprüft wird automatisch, ob Art, Uhrzeit, Personen und Serie stimmen.`,
    runSentences: 'Sätze testen',
    stop: 'Anhalten',
    running: 'Die KI arbeitet …',
    right: 'richtig',
    attempts: 'Versuche',
    whatCame: 'Was kam zurück?',
    photosTitle: 'Testsatz: Fotos',
    photosHint:
      'Ein Foto wählen – es geht an beide Modelle, nebeneinander siehst du, was erkannt wurde. Fotos werden nicht gespeichert.',
    photoKind: 'Was ist auf dem Foto?',
    photoKinds: {
      photo: 'Papierkalender, Stundenplan, Elternbrief',
      voucher: 'Gutschein',
      recipePhoto: 'Rezept',
    },
    pickPhoto: 'Foto wählen',
    found: (n: number) => (n === 1 ? '1 Eintrag erkannt' : `${n} Einträge erkannt`),
  },
  privacy:
    'Nur, was für Betrieb und Support ohnehin gespeichert ist – keine Inhalte, Zeiten nur als Tag.',
  numbersTitle: 'Zahlen',
  numbers: {
    families: 'Familien',
    testFamilies: 'davon Testfamilien',
    active7: 'aktiv in 7 Tagen',
    active30: 'aktiv in 30 Tagen',
    accounts: 'Konten',
    calendars: 'verbundene Kalender',
    waste: 'Müllkalender',
    walls: 'Wanddisplays',
    wallFamilies: 'Familien mit Wanddisplay',
    walls7: 'Wanddisplays aktiv in 7 Tagen',
    push: 'Geräte mit Push',
    aiEnabled: 'Familien mit KI',
    aiToday: 'KI-Anfragen heute',
    feedback: 'Feedback gesamt',
  },
  feedback30: 'Feedback in 30 Tagen',
  feedbackKind: { bug: 'Fehler', improvement: 'Verbesserung', feature: 'Idee' } as Record<
    string,
    string
  >,
  funnelTitle: 'Trichter',
  funnel: (u: number, c: number, i: number, j: number) =>
    `${c + u} eingetragen → ${c} bestätigt → ${i} eingeladen → ${j} beigetreten (${u} unbestätigt)`,
  bySource: 'Bestätigt je Seite',
  byLocale: 'Bestätigt je Sprache',
  familiesTitle: 'Familien',
  noFamilies: 'Noch keine Familie.',
  testFamily: 'Testfamilie',
  plus: 'Plus',
  free: 'Kostenlos (ohne Probemonat)',
  plusOn: 'Plus an',
  plusOff: 'Plus aus',
  family: (f: {
    parents: number;
    teens: number;
    kids: number;
    walls: number;
    calendars: number;
    waste: number;
    push: number;
    ai: boolean;
  }) =>
    [
      `${f.parents} Eltern`,
      `${f.teens} Teens`,
      `${f.kids} Kinder`,
      `${f.walls} ${f.walls === 1 ? 'Wand' : 'Wände'}`,
      `${f.calendars} Kalender`,
      f.waste ? 'Müll' : null,
      `${f.push} Push`,
      f.ai ? 'KI an' : 'KI aus',
    ]
      .filter(Boolean)
      .join(' · '),
  created: 'angelegt',
  lastActive: 'zuletzt aktiv',
  accountsTitle: 'Konten',
  search: 'E-Mail suchen',
  noAccounts: 'Keine Konten gefunden.',
  role: { parent: 'Elternteil', teen: 'Teen' } as Record<string, string>,
  noFamily: 'noch ohne Familie',
  unverified: 'nicht bestätigt',
  // Konten: Filter, Gruppen, Kopieren (1.18.1, Mike 13.10.)
  noFamilyGroup: 'Noch ohne Familie',
  filters: {
    verified: {
      label: 'E-Mail',
      all: 'Alle',
      yes: 'Bestätigt',
      no: 'Nicht bestätigt',
    },
    active: {
      label: 'Aktiv',
      all: 'Alle',
      d7: 'In 7 Tagen',
      d30: 'In 30 Tagen',
      older: 'Länger nicht',
    },
    group: {
      label: 'Gruppe',
      all: 'Alle',
      test: 'Testfamilie',
      normal: 'Normale Familie',
      none: 'Ohne Familie',
    },
    role: { label: 'Berechtigung', all: 'Alle', parent: 'Elternteil', teen: 'Teen' },
  },
  shown: (n: number, total: number) => `${n} von ${total} Konten`,
  copy: (n: number) => `Adressen kopieren (${n})`,
  copied: (n: number) => (n === 1 ? 'Eine Adresse kopiert.' : `${n} Adressen kopiert.`),
  copyFallback: 'Kopieren hat nicht geklappt – markiert die Adressen hier:',
  copyNote:
    'Nur für Betrieb und Support. Für Werbung oder Newsletter braucht es eine Einwilligung.',
  /** "2026-10-12" → "12.10.2026" */
  day: (d: string | null) => (d ? d.split('-').reverse().join('.') : '–'),
  loadingOverview: 'Übersicht wird geladen …',
  notFound: 'Diese Seite gibt es nicht.',
  signIn: 'Bitte zuerst anmelden.',
  loading: 'Warteliste wird geladen …',
  counts: (confirmed: number, invited: number, joined: number, unconfirmed: number) =>
    `${confirmed} bestätigt · ${invited} eingeladen · ${joined} Familien · ${unconfirmed} unbestätigt`,
  empty: 'Noch niemand hat sich bestätigt eingetragen.',
  since: 'Eingetragen',
  status: 'Stand',
  open: '–',
  invitedOn: (d: string) => `eingeladen am ${d}`,
  joinedOn: (d: string) => `Familie angelegt am ${d}`,
  invite: (n: number) => `Einladen (${n})`,
  confirmTitle: 'Einladung verschicken?',
  confirmText: (n: number) =>
    `${n === 1 ? 'Eine Familie bekommt' : `${n} Familien bekommen`} die Einladung „Ihr seid als Testfamilie dabei!“ mit ihrem persönlichen Link (30 Tage gültig). Wer schon eingeladen war, bekommt einen neuen Link – der alte gilt dann nicht mehr.`,
  send: 'Jetzt einladen',
  cancel: 'Abbrechen',
  sent: (n: number) => `${n === 1 ? 'Eine Einladung ist' : `${n} Einladungen sind`} unterwegs.`,
  select: (email: string) => `${email} auswählen`,
};
